import React, { Component } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import fill from 'lodash/fill'
import get from 'lodash/get'
import map from 'lodash/map'
import some from 'lodash/some'
import isFunction from 'lodash/isFunction'
import { Map } from 'immutable'
import compose from 'recompose/compose'
import isNumber from 'lodash/isNumber'
import findIndex from 'lodash/findIndex'
import { connect as connectRedux } from 'react-redux'
import Link from 'components/Link'
import Button, { TYPES } from 'components/Button'
import Icon, { ICON_TYPES } from 'components/Icon.v2'
import UniqueId from 'components/UniqueId'
import { H3 } from 'components/Heading'
import RowAccessor from './_RowAccessor'

function noop () {
  // do nothing
}

export const STYLES = {
  S12345: 's12345',
  S12334: 's12334',
  S12456: 's12456',
  S12234: 's12234',
  S123: 's123',
  S034: 's034',
  S23456: 's23456',
  TOPICS_LARGE: 'topics-large',
  TOPICS_SMALL: 'topics-small',
  TOPICS_TABLET: 'topics-tablet',
  SINGLE: 'single',
  TILES_VERTICAL: 'tiles-vertical',
  TILES_GAIA_PLUS: 'tiles-gaia-plus',
  TILES_TOP_TEN: 'tiles-top-ten',
  TILES_TOP_TEN_VERTICAL: 'tiles-top-ten-vertical',
  TILES_PRODUCT: 'tiles-product',
}

function getItem (node) {
  if (node.tagName === 'LI') return node
  return getItem(node.parentNode)
}

function getItemIndex (item, container) {
  try {
    const items = container.querySelectorAll('.row-v2__item')
    return findIndex(items, (i) => i === item)
  } catch (_) {
    // If the query selector fails just return the default findIndex value
    return -1
  }
}

function getTransform (index, max, limit) {
  const fullWidth = (max / limit) * 100
  const itemWidth = fullWidth / max
  const left = itemWidth * index
  return { transform: `translate3d(-${left}%,0,0)` }
}

function getRootClass (
  {
    asGrid,
    disableScale,
    className,
    style,
  },
  {
    hoverIndex,
  },
) {
  const cls = ['row-v2']
  if (isNumber(hoverIndex)) cls.push('row-v2--hovering')
  if (className) cls.push(`row-v2--${className}`)
  if (style) cls.push(`row-v2--${style}`)
  if (asGrid) cls.push('row-v2--grid')
  if (disableScale) cls.push('row-v2--disable-scale')
  return cls.join(' ')
}

function getLabel ({
  label, description, ctaLabel, ctaOnClick, ctaUrl,
}) {
  const modifier = description ? ' row-v2__label--nomargin' : ''
  return label || ctaLabel ? (
    <H3 className={`row-v2__label${modifier}`}>
      {label ? (
        <span className="row-v2__label-text">
          {label}
        </span>
      ) : null}
      {ctaLabel ? (
        <span className="row-v2__label-cta">
          {
            ctaUrl
              ? <Link
                to={ctaUrl}
                role="link"
                className="row-v2__cta"
                onClick={ctaOnClick}
                scrollToTop
              >{ctaLabel}
              </Link>
              : <Button
                type={TYPES.LINK}
                onClick={ctaOnClick}
                className="row-v2__cta"
              >
                {ctaLabel}
              </Button>
          }
        </span>
      ) : null}
    </H3>
  ) : null
}

function getDescription ({ description }) {
  return description ? (
    <p className="row-v2__description">
      {description}
    </p>
  ) : null
}

class RowV2 extends Component {
  constructor (props) {
    super(props)
    this.state = {
      contentTransform: null,
      showControls: false,
      hoverIndex: null,
      limit: null,
      index: 0,
    }
  }

  componentDidMount () {
    const { props } = this
    const { items } = props
    const refreshListeners = items.size > 0
    this.configure({ refreshListeners })
  }

  componentDidUpdate (prevProps) {
    const { props } = this
    const { items, viewportWidth } = props
    const { viewportWidth: prevWidth, items: prevItems } = prevProps
    const refreshListeners = !items.equals(prevItems)

    if (refreshListeners || viewportWidth !== prevWidth) {
      this.configure({ refreshListeners })
    }

    if (!items.equals(prevProps.items) && items.size > 0) {
      this.setupObserver()
    }
  }

  componentWillUnmount = () => {
    this.removeIntersectionObserver()
    this._component = undefined
  }

  onHoverEnter = (e) => {
    const { props, state } = this
    const { index, limit } = state
    const { onHover, items } = props
    const { currentTarget } = e

    const item = getItem(currentTarget)
    const hoverIndex = this.$container ? getItemIndex(item, this.$container) : -1
    const offstage = hoverIndex >= (index + limit) || hoverIndex < index
    const isFocusEvent = currentTarget !== item

    onHover({
      target: currentTarget,
      leaving: false,
      entering: true,
      isFocusEvent,
      item,
    })

    if (isFocusEvent && offstage) {
      this.setState(() => ({
        contentTransform: getTransform(hoverIndex, items.size, limit),
        index: hoverIndex,
        hoverIndex,
      }))
      return
    }

    this.setState(() => ({
      hoverIndex,
    }))
  }

  onHoverLeave = (e) => {
    const { props } = this
    const { onHover } = props
    const { currentTarget } = e
    const item = getItem(currentTarget)

    if (onHover) {
      onHover({
        target: currentTarget,
        entering: false,
        leaving: true,
        item,
      })
    }
    this.setState(() => ({
      hoverIndex: undefined,
    }))
  }

  moveForward = (e) => { // eslint-disable-line react/sort-comp
    e.preventDefault()
    const { state, props } = this
    const { index, limit } = state
    const { items } = props
    const total = items.size

    let nextIndex = index + limit

    if ((nextIndex + limit) >= total) {
      nextIndex = total - limit
    }

    if (nextIndex < 0) {
      nextIndex = 0
    }

    this.setState(() => ({
      contentTransform: getTransform(nextIndex, total, limit),
      index: nextIndex,
    }))
  }

  moveBack = (e) => {
    e.preventDefault()
    const { state, props } = this
    const { index, limit } = state
    const { items } = props

    let nextIndex = index - limit

    if (nextIndex < 0) {
      nextIndex = 0
    }
    this.setState(() => ({
      contentTransform: getTransform(nextIndex, items.size, limit),
      index: nextIndex,
    }))
  }

  getItemTransformClass (itemIndex) {
    const { state, props } = this
    const { hoverIndex, limit, index } = state
    const { disableScale } = props
    //
    // if nothing is hovering, there is nothing for us to do
    // return undefined so that React style props is set back to its default
    if (!isNumber(hoverIndex)) {
      return null
    }

    const isFirstInView = hoverIndex === index
    const isLastInView = hoverIndex === (index + (limit - 1))

    // disable the scaling when hovering
    // but add a different class that can be used for first and last
    // in other components, like tile v2
    if (disableScale) {
      if (itemIndex === hoverIndex) {
        return (isFirstInView && 'row-v2__item--hovered-first')
          || (isLastInView && 'row-v2__item--hovered-last')
      }
      return null
    }

    // we are currently in a hover state
    // if we are dealing with the currently hovered element
    if (itemIndex === hoverIndex) {
      return (isFirstInView && 'row-v2__item--hover-first')
        || (isLastInView && 'row-v2__item--hover-last')
        || 'row-v2__item--hover'
    }

    if (itemIndex < hoverIndex) {
      return (isFirstInView && 'row-v2__item--before-first')
        || (isLastInView && 'row-v2__item--before-last')
        || 'row-v2__item--before'
    }

    return (isFirstInView && 'row-v2__item--after-first')
      || (isLastInView && 'row-v2__item--after-last')
      || 'row-v2__item--after'
  }

  /**
   * Set the react dom element when it is created
   * @param {Element} component A DOM element
   */
  onRef = (component) => {
    this._component = component

    // verify that the items list has data
    if (this.props.items.size > 0) {
      this.setupObserver()
    }
  }

  /**
   * Sets up the IntersectionObserver for the DOM element.
   * Checks if the component and callback are valid before observing.
   */
  setupObserver () {
    const { visibilityThreshold, onVisibilityChange } = this.props
    this.removeIntersectionObserver()
    if (!isFunction(onVisibilityChange) || !this._component || !global.IntersectionObserver) {
      return
    }
    this._observer = new IntersectionObserver(this.onIntersectionObserverVisibility, {
      // Use viewport
      root: null,
      rootMargin: '0px',
      threshold: visibilityThreshold,
    })
    this._observer.observe(this._component)
  }

  /**
   * Trigger the onVisibilityChange action when the component
   * is visible
   */
  onIntersectionObserverVisibility = (entries) => {
    const { onVisibilityChange, items } = this.props
    if (!isFunction(onVisibilityChange)) {
      return
    }
    // Loop through entires to see if one is visible
    const isVisible = some(entries, (entry) => get(entry, 'isIntersecting'))
    onVisibilityChange(isVisible, items)
  }

  setContainer = (el) => {
    this.$container = el
  }

  getRenderableItems () {
    const { props, state } = this
    const { index, limit } = state
    const { items, static: isStatic } = props
    if (isStatic && limit) {
      return items.slice(index, limit)
    }
    return items
  }

  /**
   * Remove the intersection observer if it exists
   */
  removeIntersectionObserver = () => {
    if (this._observer && this._component) {
      this._observer.unobserve(this._component)
      this._observer = undefined
    }
  }

  setFirstItem = (el) => {
    this.$firstItem = el
  }

  getItemClass (itemIndex) {
    const { state, props } = this
    const { index, limit } = state
    const { disableScale } = props
    const cls = ['row-v2__item']

    if (limit !== null
      && (itemIndex < index || itemIndex >= index + limit)) {
      cls.push('row-v2__item--hidden')
    } else {
      cls.push('row-v2__item--visible')
    }

    if (disableScale) {
      cls.push('row-v2__item--disable-scale')
    }

    return cls
  }

  isHidden (itemIndex) {
    const { state } = this
    const { index, limit } = state
    if (limit !== null
      && (itemIndex < index || itemIndex >= index + limit)) {
      return true
    }
    return false
  }

  configure ({ refreshListeners }) {
    //
    const { props, state } = this
    const { items, static: isStatic, asGrid } = props

    if (!asGrid && items.size && this.$container && this.$firstItem) {
      const { offsetWidth: viewport } = this.$container
      const { offsetWidth: itemWidth } = this.$firstItem
      const { index: indexFromState } = state
      const index = refreshListeners ? 0 : indexFromState

      const total = items.size
      const limit = Math.round(viewport / itemWidth)

      let nextIndex = index

      if (index + limit > total) {
        nextIndex = items.size - limit
      }

      if (nextIndex < 0) {
        nextIndex = 0
      }

      const nextState = {
        contentTransform: getTransform(nextIndex, total, limit),
        showControls: !isStatic && limit < total,
        index: refreshListeners ? index : nextIndex,
        limit,
      }
      this.setState(() => (nextState))
    }
  }

  renderCTA () {
    const { props } = this
    const { ctaLabel, ctaOnClick, ctaUrl } = props

    if (ctaLabel && ctaUrl) {
      return (
        <Link
          to={ctaUrl}
          role="link"
          className="row-v2__cta-label"
          onClick={ctaOnClick}
          scrollToTop
        >
          {ctaLabel}
        </Link>
      )
    } if (ctaLabel) {
      return (
        <Button
          type={TYPES.LINK}
          onClick={ctaOnClick}
          className="row-v2__cta-label"
        >{ctaLabel}
        </Button>
      )
    }

    return null
  }

  renderLeftControl () {
    const { state, props } = this
    const { showControls, index } = state
    const { staticText } = props

    if (showControls && index > 0) {
      return (
        <Button
          type={TYPES.LINK}
          className="row-v2__control row-v2__control--prev"
          onClick={this.moveBack}
          aria-label={staticText.getIn(['data', 'viewMore'])}
          title={staticText.getIn(['data', 'viewMore'])}
        >
          <Icon type={ICON_TYPES.CHEVRON_LEFT} />
        </Button>
      )
    }
    return null
  }

  renderRightControl () {
    const { state, props } = this
    const { showControls, index, limit } = state
    const { items, staticText } = props

    if (showControls && (index + limit) <= (items.size - 1)) {
      return (
        <Button
          type={TYPES.LINK}
          className="row-v2__control row-v2__control--next"
          onClick={this.moveForward}
          aria-label={staticText.getIn(['data', 'viewMore'])}
          title={staticText.getIn(['data', 'viewMore'])}
        >
          <Icon type={ICON_TYPES.CHEVRON_RIGHT} />
        </Button>
      )
    }
    return null
  }

  renderItem (item, itemIndex) {
    const { props, state } = this
    const { hoverIndex, uniqueId } = state
    const {
      children,
      createAccessor,
      touchable,
      asGrid,
      disableHover,
      disableScale,
    } = props
    const bindMouseEvents = !disableHover && !disableScale && !touchable && item
    const transformClass = this.getItemTransformClass(itemIndex)
    const itemClassNames = this.getItemClass(itemIndex)
    const focused = itemIndex === hoverIndex
    const key = `item-${uniqueId}-${itemIndex}`

    const params = {
      index: itemIndex,
      focused,
    }

    if (!item) {
      itemClassNames.push('row-v2__item--empty')
    }

    if (!asGrid && transformClass) {
      itemClassNames.push(transformClass)
    }

    const accessor = item
      ? createAccessor(RowAccessor, item, params)
      : null

    const accessorProps = {}

    if (bindMouseEvents) {
      accessorProps.onFocus = this.onHoverEnter
      accessorProps.onBlur = this.onHoverLeave
    }

    return (
      <li
        onMouseLeave={bindMouseEvents ? this.onHoverLeave : null}
        onMouseEnter={bindMouseEvents ? this.onHoverEnter : null}
        ref={itemIndex === 0 ? this.setFirstItem : null}
        className={itemClassNames.join(' ')}
        key={key}
      >
        <div className={`row-v2__item-inner${disableScale ? ' row-v2__item-inner--no-shadow' : ''}`}>
          {accessor && React.cloneElement(accessor, accessorProps)}
          {children(item, params)}
        </div>
      </li>
    )
  }

  renderRow () {
    const { props, state } = this
    const { uniqueId, index } = state
    const { children, onRenderContent, disableScale } = props
    const items = this.getRenderableItems()

    if (!items.size) {
      return map(fill(Array(8), 1), (n, i) => (
        <li
          className="row-v2__item row-v2__item--empty"
          key={`item-${uniqueId}-${i}`}
        >
          <div className={`row-v2__item-inner${disableScale ? ' row-v2__item-inner--no-shadow' : ''}`}>
            {children(null, { index })}
          </div>
        </li>
      ))
    }

    if (typeof onRenderContent === 'function') {
      // TODO: this should be a filter() much faster than reduce()
      // Also, firing an event on render() is a bad idea. This means there is potential to
      // dispatch action or modify state which can cause recusive rendering and react render issues!
      const visibleIndexes = items.reduce((acc, _curr, itemIndex) => {
        if (!this.isHidden(itemIndex) && _curr.size) {
          acc.push(itemIndex)
        }
        return acc
      }, [])
      onRenderContent(visibleIndexes)
    }
    return items.map((item, itemIndex) => {
      return this.renderItem(item, itemIndex)
    })
  }

  renderGrid () {
    const { props } = this
    const { items } = props
    if (!items.size) {
      return null
    }
    return items.map((item, itemIndex) => {
      return this.renderItem(item, itemIndex)
    })
  }

  render () {
    const { state, props } = this
    const { contentTransform } = state
    const { asGrid } = props

    return (
      <React.Fragment>
        {asGrid
          ? <section ref={this.onRef} className={getRootClass(props, state)}>
            {getDescription(props)}
            {getLabel(props)}
            <div ref={this.setContainer} className="row-v2__grid-wrapper">
              <ul className="row-v2__list">
                {this.renderGrid()}
              </ul>
            </div>
          </section>
          : <section ref={this.onRef} className={getRootClass(props, state)}>
            {getDescription(props)}
            {getLabel(props)}
            <div ref={this.setContainer} className="row-v2__wrapper">
              {this.renderLeftControl()}
              <div style={contentTransform} className="row-v2__content">
                <ul className="row-v2__list">
                  {this.renderRow()}
                </ul>
              </div>
              {this.renderRightControl()}
            </div>
          </section>
        }
      </React.Fragment>
    )
  }
}

RowV2.propTypes = {
  items: ImmutablePropTypes.list.isRequired,
  createAccessor: PropTypes.func.isRequired,
  children: PropTypes.func.isRequired,
  onRenderContent: PropTypes.func,
  description: PropTypes.string,
  disableHover: PropTypes.bool,
  disableScale: PropTypes.bool,
  className: PropTypes.string,
  ctaLabel: PropTypes.string,
  ctaOnClick: PropTypes.func,
  ctaUrl: PropTypes.string,
  onHover: PropTypes.func,
  label: PropTypes.string,
  static: PropTypes.bool,
  asGrid: PropTypes.bool,
  style: PropTypes.oneOf([
    STYLES.S12345,
    STYLES.S12334,
    STYLES.S12456,
    STYLES.S12234,
    STYLES.S123,
    STYLES.S034,
    STYLES.S23456,
    STYLES.s12456,
    STYLES.TOPICS_LARGE,
    STYLES.TOPICS_SMALL,
    STYLES.TOPICS_TABLET,
    STYLES.SINGLE,
    STYLES.TILES_VERTICAL,
    STYLES.TILES_GAIA_PLUS,
    STYLES.TILES_TOP_TEN,
  ]),
  onVisibilityChange: PropTypes.func,
  visibilityThreshold: PropTypes.number,
}

RowV2.defaultProps = {
  visibilityThreshold: 0.95,
  description: undefined,
  className: undefined,
  style: STYLES.S12345,
  disableHover: false,
  disableScale: false,
  label: undefined,
  ctaOnClick: noop,
  asGrid: false,
  static: false,
  onHover: noop,
  ctaLabel: '',
}

export default compose(
  UniqueId('row'),
  connectRedux(
    (state) => ({
      staticText: state.staticText.getIn(['data', 'row'], Map()),
      touchable: state.app.getIn(['viewport', 'touchable']),
      viewportWidth: state.app.getIn(['viewport', 'width']),
      auth: state.auth,
    }),
  ),
)(RowV2)
